import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui/Button.js';
import { Textarea } from '../../ui/Textarea.js';
import { Pill } from '../../PillGroup.js';
import type { PageComment } from '../../../hooks/useProjects.js';
import type { PageEditorState } from './usePageEditorState.js';

// ── Comentarios, dentro de la pestaña de herramientas ────────────────────────
//
// La otra herramienta: en vez de tocar la página, se marca algo y se pide un
// cambio. Se acumulan varios y se aplican todos juntos — de a uno serían N
// versiones y N llamadas al modelo para lo que se pidió junto.

function Nota({ children }: { children: React.ReactNode }) {
  return (
    <span style={{
      fontSize: 'var(--evo-fs-xs)',
      color: 'var(--evo-fg-3)',
      fontFamily: 'var(--evo-font)',
      lineHeight: 1.5,
      // La nota trae dos partes en líneas separadas: lo que midió el sistema y,
      // debajo, lo que dice el modelo (ver componerNota). Sin esto el salto se
      // colapsa y las dos se leen como una sola afirmación.
      whiteSpace: 'pre-line',
    }}>
      {children}
    </span>
  );
}

/** Un comentario de la lista, con su ancla y en qué quedó. */
function Fila({ comment, onRemove }: { comment: PageComment; onRemove: () => void }) {
  const { t } = useTranslation();
  const ancla = comment.kind === 'region'
    ? t('projects.comments.anchorRegion', { count: comment.targetIds.length })
    : `<${comment.tag ?? '?'}>${comment.snippet ? ` ${comment.snippet}` : ''}`;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      padding: 'var(--evo-space-2)',
      border: '1px solid var(--evo-stroke)',
      borderRadius: 'var(--evo-r-sm)',
      background: 'var(--evo-bg-2)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--evo-space-2)' }}>
        <Nota>{ancla}</Nota>
        {comment.status === 'applied' && (
          <Pill variant="outline" tone="success" size="sm">{t('projects.comments.statusApplied')}</Pill>
        )}
      </div>

      <span style={{
        fontSize: 'var(--evo-fs-xs)',
        color: 'var(--evo-fg-2)',
        fontFamily: 'var(--evo-font)',
        lineHeight: 1.5,
      }}>
        {comment.text}
      </span>

      {/* Qué dijo el modelo de este comentario. Un comentario que no se pudo
          aplicar sigue pendiente y dice por qué, en vez de quedar como hecho. */}
      {comment.note && <Nota>{comment.note}</Nota>}

      {comment.status === 'pending' && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="ghost" tone="neutral" size="sm" onClick={onRemove}>
            {t('projects.comments.remove')}
          </Button>
        </div>
      )}
    </div>
  );
}

export function CommentsSection({ editor }: { editor: PageEditorState }) {
  const { t } = useTranslation();
  const { comments, draft, dibujandoZona, addComment, removeComment, applyAllComments, isApplyingComments } = editor;
  const [texto, setTexto] = useState('');

  const pendientes = comments.filter(c => c.status === 'pending');

  const guardar = (): void => {
    const limpio = texto.trim();
    if (!limpio || !draft) return;
    addComment(limpio);
    setTexto('');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
      <Nota>
        {dibujandoZona
          ? t('projects.comments.drawing')
          : draft
            ? draft.kind === 'region'
              ? t('projects.comments.draftRegion', { count: draft.targetIds.length })
              : t('projects.comments.draftElement', { tag: draft.tag ?? '?' })
            : t('projects.comments.hint')}
      </Nota>

      {/* Escribir el comentario. Solo con algo marcado: un comentario sin ancla
          es una instrucción suelta, que es lo que ya se puede pedir por el chat. */}
      {draft && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {/* fullWidth: sin esto el Textarea toma su ancho por defecto y queda
              cortado a media caja dentro del panel, con una franja vacía a la
              derecha. Todos los demás Textarea del sistema lo pasan. */}
          <Textarea
            value={texto}
            onChange={setTexto}
            rows={3}
            fullWidth
            placeholder={t('projects.comments.placeholder')}
          />
          <Button variant="outline" tone="cyan" size="sm" disabled={texto.trim() === ''} onClick={guardar}>
            {t('projects.comments.add')}
          </Button>
        </div>
      )}

      {comments.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
          {comments.map(c => (
            <Fila key={c.id} comment={c} onRemove={() => { removeComment(c.id); }} />
          ))}
        </div>
      )}

      {pendientes.length > 0 && (
        <Button
          variant="solid"
          tone="cyan"
          size="sm"
          loading={isApplyingComments}
          onClick={applyAllComments}
        >
          {t('projects.comments.applyAll', { count: pendientes.length })}
        </Button>
      )}
    </div>
  );
}
